import { JSX, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box, Flex } from '@chakra-ui/react'
import {
  CreateRerentOrderItemRequest,
  CreateRerentOrderRequest,
  GetPriceResponse,
  OrderOrigin,
  OrderType,
} from '@leid/api-orders'
import { tryParseAsNumber } from '@leid/helpers'
import { useFormik } from 'formik'
import { useRouter } from 'next/navigation'
import * as Yup from 'yup'

import { CustomerInfo } from 'app/utils/authentication'
import { buildDate, utcNow } from 'app/utils/dates'
import { ItemApi } from 'appTypes/items'
import { CartFormValues, OrderItemInfo } from 'appTypes/orders'
import { ErrorMessage } from 'components/form/ErrorMessage/styles'
import { Input } from 'components/form/Input'
import { Button, ButtonSecondary } from 'components/ui/Buttons/Button'
import { Loader } from 'components/ui/Loader'
import apiClient from 'helpers/clients/apiClient'
import { AppDispatch } from 'store'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'

type Props = {
  cartFormValues: CartFormValues
  goToPrevStep?: VoidFunction
  product: ItemApi
  productPrice: GetPriceResponse | null
  isProlonging: boolean
  disabled?: boolean
  customerInfo: CustomerInfo
  orderItem: OrderItemInfo | undefined
}

type QuotationFormData = {
  email?: string
  phoneNumber?: string
  fullName?: string
}

const QuotationRequestForm = ({
  cartFormValues,
  goToPrevStep,
  product,
  productPrice,
  disabled,
  isProlonging,
  customerInfo,
  orderItem,
}: Props): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>()
  const router = useRouter()
  const { t } = useTranslation(['auth', 'common', 'pageTitles', 'requests'])

  const isAuthenticated = customerInfo.customer !== null

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [customerComment, setCustomerComment] = useState('')

  const onActualSubmit = async (formValues?: QuotationFormData) => {
    setIsSubmitting(true)

    const request: CreateRerentOrderRequest = {
      orderType: isProlonging ? OrderType.prolongation : OrderType.general,
      customerId: customerInfo.customer?.id,
      email: customerInfo.customer?.email ?? formValues?.email ?? '',
      phoneNumber: customerInfo.customer?.phoneNumber ?? formValues?.phoneNumber ?? '',
      fullName: customerInfo.customer?.fullName ?? formValues?.fullName ?? '',
      parentRerentOrderId: orderItem?.orderItem?.rerentOrderId,
      customerComments: customerComment,
      origin: OrderOrigin.LEID,
      organizationId: customerInfo.organization?.id,
    }

    const result = await apiClient.createRerentOrder(request)
    if (!result.success || !result.data) {
      dispatch(displaySnackbar({ message: t('common:error'), isError: true }))
      setIsSubmitting(false)
      return
    }

    const rerentOrder = result.data

    const createRerentOrderItemRequest: CreateRerentOrderItemRequest = {
      rerentOrderId: rerentOrder.id,
      locationId: cartFormValues.selectedLocationId,
      regionId: cartFormValues.selectedRegionId,
      productId: product.id,
      price: (productPrice?.rawPrice ?? 0) + (productPrice?.rebateAmount ?? 0),
      cleaningPrice: tryParseAsNumber(product.pricing?.cleaningPrice),
      insurancePrice: tryParseAsNumber(product.pricing?.insurancePrice),
      deductiblePrice: tryParseAsNumber(product.pricing?.deductiblesPrice),
      deductibleAmount: tryParseAsNumber(product.pricing?.deductiblesAmount),
      rentedFrom: buildDate(cartFormValues.startDate, cartFormValues.pickupAt),
      rentedTo: buildDate(cartFormValues.endDate, cartFormValues.returnAt),
      resignFromCleaning: true,
      resignFromInsurance: cartFormValues.includeInsurance ? !cartFormValues.includeInsurance : true,
      resignFromDeductible: cartFormValues.includeDeductibles ? !cartFormValues.includeDeductibles : true,
      rerentOrderItemId: orderItem?.orderItem?.rerentOrderItemId,
      sendCustomerRequest: true,
    }
    const rerentOrderItemResult = await apiClient.createRerentOrderItem(createRerentOrderItemRequest)
    if (!rerentOrderItemResult.success || !rerentOrderItemResult.data) {
      dispatch(displaySnackbar({ message: t('common:error'), isError: true }))
      setIsSubmitting(false)
      return
    }

    const newRerentOrderItem = rerentOrderItemResult.data

    const response = await apiClient.bootstrapOfferOrderProlongation({
      requestOrderId: rerentOrder.id,
      items: [
        {
          requestOrderItemId: newRerentOrderItem.id,
          price: newRerentOrderItem.price,
          rentedTo: newRerentOrderItem.rentedTo ?? utcNow(),
        },
      ],
    })
    if (!response.success || !response.data) {
      dispatch(displaySnackbar({ message: t('common:error'), isError: true }))
      setIsSubmitting(false)
      return
    }

    dispatch(displaySnackbar({ message: t('common:requestWasSent'), isError: false }))
    router.push('/browse')
  }

  const { getFieldProps, touched, errors, handleSubmit } = useFormik<QuotationFormData>({
    initialValues: {
      email: '',
      phoneNumber: '',
      fullName: '',
    },
    onSubmit: async (formValues) => {
      onActualSubmit(formValues)
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().email(t('common:invalidEmail')).required(t('common:required')),
      phoneNumber: Yup.number()
        .test('len', t('common:phoneNumberValidation'), (val) => (val ? val.toString().length === 8 : false))
        .required(t('common:required'))
        .typeError(t('common:validationNumber')),
      fullName: Yup.string().required(t('common:required')),
    }),
    validateOnMount: true,
  })

  const onSumbit = () => {
    if (!isAuthenticated) {
      // Let formik to do the job
      handleSubmit()
      return
    }

    onActualSubmit()
  }

  if (isProlonging && !orderItem?.orderItem?.rerentOrderId) {
    return <Box marginY="30px">{<ErrorMessage>{t('common:error')}</ErrorMessage>}</Box>
  }

  return (
    <>
      <Box padding={isAuthenticated ? 0 : 5}>
        {!isAuthenticated && (
          <Box marginTop="36px">
            <Box marginTop="20px">
              <Input
                {...getFieldProps('email')}
                placeholder={t('common:email')}
                name="email"
                touched={touched.email}
                error={errors.email}
                label={t('common:email')}
              />
            </Box>
            <Box marginTop="20px">
              <Input
                {...getFieldProps('phoneNumber')}
                placeholder={t('common:phoneNumber')}
                name="phoneNumber"
                touched={touched.phoneNumber}
                error={errors.phoneNumber}
                type="number"
                label={t('common:phoneNumber')}
                adornmentLeft="+47"
                paddingLeft="35px"
              />
            </Box>
            <Box marginTop="20px">
              <Input
                {...getFieldProps('c')}
                placeholder={t('common:fullName')}
                name="fullName"
                touched={touched.fullName}
                error={errors.fullName}
                label={t('common:fullName')}
              />
            </Box>
          </Box>
        )}

        {isSubmitting ? (
          <Loader />
        ) : (
          <>
            <Box marginBottom="12px">
              <Input
                name="customerComment"
                value={customerComment}
                onChange={(e) => {
                  setCustomerComment(e.target.value)
                }}
                placeholder={t('requests:comment')}
                type="textarea"
              />
            </Box>

            <Flex marginTop={isAuthenticated ? 0 : '20px'}>
              {goToPrevStep && (
                <Box flex={1} maxWidth={{ lg: '200px' }}>
                  <ButtonSecondary onClick={goToPrevStep}>{t('common:editRentalPeriod')}</ButtonSecondary>
                </Box>
              )}
              <Box flex={1} marginLeft="10px">
                <Button onClick={onSumbit} isDisabled={disabled === true}>
                  {t('common:requestQuotationButton')}
                </Button>
              </Box>
            </Flex>
          </>
        )}
      </Box>
    </>
  )
}

export default QuotationRequestForm
